import { useState, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { PLATFORMS, PLATFORM_LABELS } from '@affily/shared/constants';
import { formatCount, formatCents, formatPercent } from '@affily/shared/money';
import { api } from '../../lib/api.js';
import { Seo } from '../../components/Seo.jsx';
import { Button, Field, Input, Textarea, Select, Alert } from '../../components/ui.jsx';

/**
 * Media kit generator.
 *
 * The kit is composed and previewed with no gate at all — you can build it,
 * read it, and screenshot it without giving us anything. The email is asked for
 * only at the point of downloading a file, and the download proceeds even if
 * saving the lead fails: the person did their part.
 */

const EMPTY = {
  name: '',
  headline: '',
  location: '',
  email: '',
  bio: '',
  platforms: [{ platform: 'instagram', handle: '', followers: '', avgViews: '', engagement: '' }],
  rates: [{ deliverable: '', amount: '' }],
  audience: '',
  brands: '',
};

export default function MediaKitGenerator() {
  const [form, setForm] = useState(EMPTY);
  const [gateOpen, setGateOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const kitRef = useRef(null);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const updateRow = (key, i, patch) =>
    setForm((f) => ({
      ...f,
      [key]: f[key].map((row, idx) => (idx === i ? { ...row, ...patch } : row)),
    }));

  const totalFollowers = useMemo(
    () => form.platforms.reduce((sum, p) => sum + (Number(p.followers) || 0), 0),
    [form.platforms],
  );

  async function download(email) {
    setBusy(true);
    setError('');
    try {
      // Fire and forget: a lead-capture failure must not cost someone their
      // download. They gave us the address; we owe them the file either way.
      api
        .post('/leads', {
          email,
          source: 'media-kit',
          name: form.name || undefined,
          context: { totalFollowers, platforms: form.platforms.map((p) => p.platform) },
        })
        .catch(() => {});

      const [{ toPng }, { jsPDF }] = await Promise.all([
        import('html-to-image'),
        import('jspdf'),
      ]);

      const node = kitRef.current;
      const dataUrl = await toPng(node, {
        pixelRatio: 2,
        backgroundColor: '#ffffff',
        cacheBust: true,
      });

      const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const ratio = node.offsetHeight / node.offsetWidth;
      const imgWidth = pageWidth - 48;
      const imgHeight = imgWidth * ratio;

      pdf.addImage(dataUrl, 'PNG', 24, 24, imgWidth, Math.min(imgHeight, pageHeight - 48));
      pdf.save(`${(form.name || 'media-kit').toLowerCase().replace(/\s+/g, '-')}-media-kit.pdf`);

      setGateOpen(false);
    } catch (err) {
      setError(`Could not build the PDF: ${err.message}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Seo
        title="Free media kit generator for creators"
        description="Build a clean, professional media kit in a couple of minutes and download it as a PDF. Free, no signup to build it."
      />

      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6">
        <p className="text-2xs font-semibold uppercase tracking-widest text-accent">Free tool</p>
        <h1 className="mt-2 text-4xl font-semibold tracking-tight">Media kit generator</h1>
        <p className="mt-3 max-w-xl text-ink-muted">
          Fill in the form, watch it build on the right, download it as a PDF. Building and reading
          it costs nothing — we only ask for an email when you want the file.
        </p>

        <div className="mt-10 grid gap-8 lg:grid-cols-[minmax(0,24rem)_minmax(0,1fr)]">
          {/* ── Form ──────────────────────────────────────────────────── */}
          <form className="space-y-5" onSubmit={(e) => e.preventDefault()}>
            <Field label="Name" required>
              <Input value={form.name} onChange={set('name')} placeholder="Maya Okonjo" />
            </Field>
            <Field label="Headline">
              <Input
                value={form.headline}
                onChange={set('headline')}
                placeholder="Skincare routines for oily, acne-prone skin"
              />
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Location">
                <Input value={form.location} onChange={set('location')} placeholder="Lisbon" />
              </Field>
              <Field label="Contact email">
                <Input
                  type="email"
                  value={form.email}
                  onChange={set('email')}
                  placeholder="hi@example.com"
                />
              </Field>
            </div>
            <Field label="About you">
              <Textarea value={form.bio} onChange={set('bio')} rows={4} />
            </Field>

            <fieldset className="space-y-3">
              <legend className="text-sm font-medium">Platforms</legend>
              {form.platforms.map((p, i) => (
                <div key={i} className="rounded-lg border border-rule bg-paper-raised p-3">
                  <div className="grid gap-3 sm:grid-cols-2">
                    <Field label="Platform">
                      <Select
                        value={p.platform}
                        onChange={(e) => updateRow('platforms', i, { platform: e.target.value })}
                      >
                        {PLATFORMS.slice(0, 8).map((pl) => (
                          <option key={pl} value={pl}>
                            {PLATFORM_LABELS[pl]}
                          </option>
                        ))}
                      </Select>
                    </Field>
                    <Field label="Handle">
                      <Input
                        value={p.handle}
                        onChange={(e) => updateRow('platforms', i, { handle: e.target.value })}
                        placeholder="@handle"
                      />
                    </Field>
                  </div>
                  <div className="mt-3 grid gap-3 sm:grid-cols-3">
                    <Field label="Followers">
                      <Input
                        type="number"
                        min="0"
                        value={p.followers}
                        onChange={(e) => updateRow('platforms', i, { followers: e.target.value })}
                      />
                    </Field>
                    <Field label="Avg. views">
                      <Input
                        type="number"
                        min="0"
                        value={p.avgViews}
                        onChange={(e) => updateRow('platforms', i, { avgViews: e.target.value })}
                      />
                    </Field>
                    <Field label="Engagement %">
                      <Input
                        type="number"
                        min="0"
                        step="0.1"
                        value={p.engagement}
                        onChange={(e) => updateRow('platforms', i, { engagement: e.target.value })}
                      />
                    </Field>
                  </div>
                  {form.platforms.length > 1 && (
                    <button
                      type="button"
                      onClick={() =>
                        setForm((f) => ({
                          ...f,
                          platforms: f.platforms.filter((_, x) => x !== i),
                        }))
                      }
                      className="mt-2 text-xs text-negative hover:underline"
                    >
                      Remove
                    </button>
                  )}
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  setForm((f) => ({
                    ...f,
                    platforms: [
                      ...f.platforms,
                      { platform: 'tiktok', handle: '', followers: '', avgViews: '', engagement: '' },
                    ],
                  }))
                }
              >
                Add platform
              </Button>
            </fieldset>

            <fieldset className="space-y-3">
              <legend className="text-sm font-medium">Rates</legend>
              {form.rates.map((r, i) => (
                <div key={i} className="flex items-end gap-3">
                  <Field label="Deliverable" className="flex-1">
                    <Input
                      value={r.deliverable}
                      onChange={(e) => updateRow('rates', i, { deliverable: e.target.value })}
                      placeholder="TikTok video"
                    />
                  </Field>
                  <Field label="From (USD)" className="w-32">
                    <Input
                      type="number"
                      min="0"
                      value={r.amount}
                      onChange={(e) => updateRow('rates', i, { amount: e.target.value })}
                    />
                  </Field>
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  setForm((f) => ({ ...f, rates: [...f.rates, { deliverable: '', amount: '' }] }))
                }
              >
                Add rate
              </Button>
            </fieldset>

            <Field label="Audience" hint="Top countries, age split — whatever you know.">
              <Textarea
                value={form.audience}
                onChange={set('audience')}
                rows={3}
                placeholder="48% US, 14% UK · 41% aged 18–24"
              />
            </Field>

            <Field label="Brands you’ve worked with">
              <Textarea
                value={form.brands}
                onChange={set('brands')}
                rows={2}
                placeholder="Lumen Skin, Field Coffee, Norra"
              />
            </Field>

            <div className="border-t border-rule pt-5">
              <Button
                type="button"
                size="lg"
                className="w-full"
                onClick={() => setGateOpen(true)}
                disabled={!form.name}
              >
                Download as PDF
              </Button>
              {!form.name && (
                <p className="mt-1.5 text-xs text-ink-faint">Add your name to enable the download.</p>
              )}
            </div>
          </form>

          {/* ── Live preview ──────────────────────────────────────────── */}
          <div>
            <p className="mb-2 text-2xs font-semibold uppercase tracking-wider text-ink-faint">
              Preview
            </p>
            <div className="overflow-x-auto rounded-lg border border-rule shadow-card">
              <MediaKit ref={kitRef} data={form} totalFollowers={totalFollowers} />
            </div>
          </div>
        </div>

        {gateOpen && (
          <EmailGate
            busy={busy}
            error={error}
            onClose={() => setGateOpen(false)}
            onSubmit={download}
          />
        )}

        <section className="mt-16 max-w-2xl">
          <h2 className="text-xl font-semibold tracking-tight">
            A media kit is a snapshot. A profile is a listing.
          </h2>
          <p className="mt-3 text-ink-muted">
            A PDF is out of date the day after you send it, and it only reaches brands you already
            know how to email. A public Affily profile carries the same numbers, stays current, and
            is what brands filter on when they’re looking for someone like you.
          </p>
          <Button to="/signup?role=creator" className="mt-4">
            Create a free profile
          </Button>
          <p className="mt-3 text-sm text-ink-muted">
            Or{' '}
            <Link to="/creators" className="text-accent hover:underline">
              see what other creators publish
            </Link>{' '}
            first.
          </p>
        </section>
      </div>
    </>
  );
}

function EmailGate({ onSubmit, onClose, busy, error }) {
  const [email, setEmail] = useState('');

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="gate-title"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="w-full max-w-sm rounded-lg border border-rule bg-paper-raised p-5 shadow-raised">
        <h2 id="gate-title" className="text-lg font-semibold">
          Where should we send it?
        </h2>
        <p className="mt-1.5 text-sm text-ink-muted">
          The PDF downloads straight to your device. We keep the address to tell you when something
          relevant ships — one email, unsubscribe from it, done.
        </p>

        <form
          className="mt-4 space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit(email);
          }}
        >
          <Alert>{error}</Alert>
          <Field label="Email" required>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
              placeholder="you@example.com"
            />
          </Field>
          <div className="flex gap-2">
            <Button type="submit" className="flex-1" disabled={busy}>
              {busy ? 'Building PDF…' : 'Download PDF'}
            </Button>
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * The kit itself. Rendered with inline-ish styling and a white background
 * because html-to-image rasterises exactly what it sees — a themed surface
 * would produce a PDF that looks wrong on paper.
 */
function MediaKit({ ref, data, totalFollowers }) {
  const rates = data.rates.filter((r) => r.deliverable && r.amount);
  const platforms = data.platforms.filter((p) => p.handle || p.followers);

  return (
    <div ref={ref} className="w-[46rem] bg-white p-10 text-[#1a1c22]">
      <header className="flex items-start justify-between gap-6 border-b-2 border-[#1a1c22] pb-5">
        <div>
          <h1 className="text-4xl font-semibold tracking-tight">{data.name || 'Your name'}</h1>
          {data.headline && <p className="mt-1.5 text-lg text-[#5a5f6e]">{data.headline}</p>}
          <p className="mt-2 text-sm text-[#5a5f6e]">
            {[data.location, data.email].filter(Boolean).join(' · ')}
          </p>
        </div>
        <div className="shrink-0 text-right">
          <p className="text-[0.6875rem] font-semibold uppercase tracking-wider text-[#8b8f9c]">
            Total audience
          </p>
          <p className="text-3xl font-semibold tabular-nums">{formatCount(totalFollowers)}</p>
        </div>
      </header>

      {data.bio && <p className="mt-6 leading-relaxed text-[#3c414e]">{data.bio}</p>}

      {platforms.length > 0 && (
        <section className="mt-8">
          <h2 className="text-[0.6875rem] font-semibold uppercase tracking-wider text-[#8b8f9c]">
            Platforms
          </h2>
          <table className="mt-2 w-full text-sm">
            <thead>
              <tr className="border-b border-[#d9dbe1] text-left text-[#5a5f6e]">
                <th className="py-1.5 font-medium">Platform</th>
                <th className="py-1.5 font-medium">Handle</th>
                <th className="py-1.5 text-right font-medium">Followers</th>
                <th className="py-1.5 text-right font-medium">Avg. views</th>
                <th className="py-1.5 text-right font-medium">Engagement</th>
              </tr>
            </thead>
            <tbody>
              {platforms.map((p, i) => (
                <tr key={i} className="border-b border-[#eceef2]">
                  <td className="py-2 font-medium">{PLATFORM_LABELS[p.platform] ?? p.platform}</td>
                  <td className="py-2 text-[#5a5f6e]">{p.handle || '—'}</td>
                  <td className="py-2 text-right tabular-nums">
                    {p.followers ? formatCount(Number(p.followers)) : '—'}
                  </td>
                  <td className="py-2 text-right tabular-nums">
                    {p.avgViews ? formatCount(Number(p.avgViews)) : '—'}
                  </td>
                  <td className="py-2 text-right tabular-nums">
                    {p.engagement ? formatPercent(Number(p.engagement), { digits: 1 }) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-1.5 text-[0.6875rem] text-[#8b8f9c]">
            Self-reported by the creator.
          </p>
        </section>
      )}

      <div className="mt-8 grid grid-cols-2 gap-8">
        {rates.length > 0 && (
          <section>
            <h2 className="text-[0.6875rem] font-semibold uppercase tracking-wider text-[#8b8f9c]">
              Rates
            </h2>
            <table className="mt-2 w-full text-sm">
              <tbody>
                {rates.map((r, i) => (
                  <tr key={i} className="border-b border-[#eceef2]">
                    <td className="py-1.5">{r.deliverable}</td>
                    <td className="py-1.5 text-right font-medium tabular-nums">
                      from {formatCents(Math.round(Number(r.amount) * 100))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}

        <div className="space-y-6">
          {data.audience && (
            <section>
              <h2 className="text-[0.6875rem] font-semibold uppercase tracking-wider text-[#8b8f9c]">
                Audience
              </h2>
              <p className="mt-1.5 whitespace-pre-line text-sm text-[#3c414e]">{data.audience}</p>
            </section>
          )}
          {data.brands && (
            <section>
              <h2 className="text-[0.6875rem] font-semibold uppercase tracking-wider text-[#8b8f9c]">
                Worked with
              </h2>
              <p className="mt-1.5 text-sm text-[#3c414e]">{data.brands}</p>
            </section>
          )}
        </div>
      </div>

      <footer className="mt-10 border-t border-[#d9dbe1] pt-3 text-[0.6875rem] text-[#8b8f9c]">
        Made with affily.me — the marketplace for creators and brands.
      </footer>
    </div>
  );
}
